import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    styled, Box, IconButton, TextField, Checkbox, FormControlLabel, FormGroup, Button,
    FormControl, Avatar, Select, MenuItem, Slider
} from '@mui/material';
// import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useQuantityContext } from '../../QuantityContext';
import useApi from '../../Utilities/useAPI';
import GroupsIcon from '@mui/icons-material/Groups';
import AvatarGroup from '@mui/material/AvatarGroup';
import VerifiedIcon from '@mui/icons-material/Verified';
import { ReactComponent as NonVerifiedTickNew } from "../../../Assets/img/icons/Non-verified-icon.svg";
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { useUserDetailContext } from '../../UserDetailContext';

const SkillBox = styled(Box)({
    border: "1px solid #D9D9D9",
    height: '32px',
    borderRadius: '16px',
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    margin: '6px 6px 6px 0px',
    gap: '5px',
    fontSize: '14px',
    lineHeight: '20.02px'
});

function Connect() {
    const [searchTerm, setSearchTerm] = useState("");
    const { callApiFunction } = useApi();
    const [users, setUsers] = useState([]);
    const [userStatus, setUserStatus] = useState(null);
    const [filteredMembers, setFilteredMembers] = useState(users);
    const [selectedUser, setSelectedUser] = useState(null);
    const objectId = localStorage.getItem("objectId");
    const { userDetails } = useUserDetailContext();

    const fetchGroupData = async () => {
        try {
            const params = {
                searchParam: '',
                limit: 20,
                offset: 1,
            };

            if (searchTerm && searchTerm.trim() !== "") {
                params.searchParam = searchTerm;
            }
            const response = await callApiFunction('getAllNeoinUsers', params);

            if (response.status === "SUCCESS") {
                const membersWithImages = response.data.results.map((member) => {
                    // Role logic
                    const role = member.designation || member.field_of_study || member.title || '---';  // Updated role logic
                    const subTitle = member.profession_department || member.institute || member.nickname || '---';  // SubTitle logic

                    return {
                        ...member,
                        is_verified: member.is_verified ? true : false,
                        img: member.profile_url,  // Use member.profile_url as img
                        role: role,  // Include role logic
                        subTitle: subTitle  // Include subTitle logic
                    };
                });
                setUsers(membersWithImages);
                setFilteredMembers(membersWithImages);
            } else {
                console.error('Failed to fetch group data.');
            }
        } catch (err) {
            console.error('An error occurred while fetching group data.');
        }
    };

    const handleApply = async (apiPayload) => {
        try {
            const params = {
                filters: apiPayload,
                limit: 20,
                offset: 1,
            };
            const response = await callApiFunction('getFilteredUsersDetails', params);

            if (response.status === "SUCCESS") {
                const membersWithImages = response.data.results.map((member) => {
                    // Role logic
                    const role = member.designation || member.field_of_study || member.title || '---';  // Updated role logic
                    const subTitle = member.profession_department || member.institute || member.nickname || '---';  // SubTitle logic

                    return {
                        ...member,
                        is_verified: member.is_verified ? true : false,
                        img: member.profile_url,  // Use member.profile_url as img
                        role: role,  // Include role logic
                        subTitle: subTitle  // Include subTitle logic
                    };
                });
                setUsers(membersWithImages);
                setFilteredMembers(membersWithImages);
            } else {
                console.error('Failed to fetch group data.');
            }
        } catch (err) {
            console.error('An error occurred while fetching group data.');
        }
    };

    // Fetch user data once and store it in context
    useEffect(() => {
        fetchGroupData();
    }, []);

    useEffect(() => {
        const debounceSearch = setTimeout(() => {
            fetchGroupData();  // Call API regardless of searchTerm
        }, 300);  // Wait 300ms after the user stops typing

        return () => clearTimeout(debounceSearch);  // Cleanup debounce on every keystroke
    }, [searchTerm]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleUserClick = (user) => {
        if (selectedUser && selectedUser.id === user.id) {
            setSelectedUser(null);
        } else {
            setSelectedUser(user);
            setUserStatus(user.status);
        }
    };


    const handleStatusUpdate = (status, referenceId) => {
        setUserStatus(status);
        const updatedMembers = filteredMembers.map((member) =>
            member.reference_id === referenceId ? { ...member, status } : member
        );
        setFilteredMembers(updatedMembers);

        if (selectedUser && selectedUser.reference_id === referenceId) {
            setSelectedUser({ ...selectedUser, status });
        }
    };

    const handleConnectRequest = async (contactRefId, memberIndex) => {
        const payload = {
            userrefid: objectId, // Replace with your actual objectId
            contact_ref_id: contactRefId,
            relationship_type: "FRIEND",
        };

        try {
            const response = await callApiFunction('SendConnectRequest', payload);

            if (response.status === "SUCCESS") {
                const updatedMembers = [...filteredMembers];
                updatedMembers[memberIndex].status = "PENDING";
                setFilteredMembers(updatedMembers);
                handleStatusUpdate("PENDING", contactRefId);
            } else {
                console.error('Failed to send connection request.');
            }
        } catch (err) {
            console.error('An error occurred while sending the connection request:', err);
        }
    };

    const allMembers = filteredMembers.filter(
        (member) =>
            `${member.first_name} ${member.last_name}` !== userDetails.name // Filter out the current user
    );

    return (
        <div className="outer-layer-of-contacts" style={{ position: 'relative' }}>
            <div className="outer-container">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className='fw-bold' style={{ fontSize: "18px" }}>
                        Find and connect with people to grow your network.
                    </h5>
                    <div>
                        <TextField
                            placeholder="Search for People by name"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            sx={{ width: 320 }}
                        />
                    </div>
                </div>
                {selectedUser && (
                    <MiniProfileView user={selectedUser} onStatusUpdate={handleStatusUpdate} />
                )}
                <div className='connections_sugg'>Suggested Connections</div>

                <div className="avatars-container">
                    {/* {filteredMembers.map((member, index) => (
                        <div key={index} onClick={() => handleUserClick(member)}>
                            <div className="avatar-box-connect pointer">
                                <div className="avatar-details-connect">
                                    <div>
                                        <div className="avatar-image t" style={{ width: "56px", height: "56px" }}>
                                            <Avatar
                                                className="w-100 h-100 cover-image"
                                                sx={{ width: 56, height: 56 }}
                                                src={member.img} alt={member?.first_name}>
                                                {member?.first_name?.charAt(0).toUpperCase()}
                                            </Avatar>
                                        </div>
                                        <div className={`symbol_of_contacts compact flex-center`}>
                                            {member.is_verified ?
                                                <VerifiedIcon style={{ color: "#43A047" }} /> :
                                                <NonVerifiedTickNew />
                                            }
                                        </div>
                                    </div>
                                    <div className="member-info-connect">
                                        <div className="member-name-connect">{member.first_name} {member.last_name}</div>
                                        <div className="member-role-connect">{member.role}</div>

                                        {member.status === null && (
                                            <Button
                                                className="connect-button"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent handleUserClick from being called
                                                    handleConnectRequest(member.reference_id, index);
                                                }}
                                            >
                                                CONNECT
                                            </Button>
                                        )}

                                        {member.status === "PENDING" && <Button className="connect-button">SENT</Button>}
                                        {member.status === "ACCEPTED" && <Button className="connect-button">MESSAGE</Button>}
                                    </div>
                                </div>
                            </div>
                        </div>

                    ))} */}

                    {allMembers.map((member, index) => (
                        <div key={index} onClick={() => handleUserClick(member)}>
                            <div className="avatar-box-connect pointer">
                                <div className="avatar-details-connect">
                                    <div>
                                        <div className="avatar-image t" style={{ width: "56px", height: "56px" }}>
                                            <Avatar
                                                className="w-100 h-100 cover-image"
                                                sx={{ width: 56, height: 56 }}
                                                src={member.img}
                                                alt={member?.first_name}
                                            >
                                                {member?.first_name?.charAt(0).toUpperCase()}
                                            </Avatar>
                                        </div>
                                        <div className={`symbol_of_contacts compact flex-center`}>
                                            {member.is_verified ? (
                                                <VerifiedIcon style={{ color: "#43A047" }} />
                                            ) : (
                                                <NonVerifiedTickNew />
                                            )}
                                        </div>
                                    </div>
                                    <div className="member-info-connect">
                                        <div className="member-name-connect">
                                            {member.first_name} {member.last_name}
                                        </div>
                                        <div className="member-role-connect">{member.role}</div>

                                        {member?.status === null && (
                                            <Button
                                                className="connect-button"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent handleUserClick from being called
                                                    handleConnectRequest(member.reference_id, index);
                                                }}
                                            >
                                                CONNECT
                                            </Button>
                                        )}
                                        {member?.status === "REJECTED" && (
                                            <Button
                                                className="connect-button"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent handleUserClick from being called
                                                    handleConnectRequest(member.reference_id, index);
                                                }}
                                            >
                                                CONNECT
                                            </Button>
                                        )}

                                        {member.status === "PENDING" && <Button className="connect-button">SENT</Button>}
                                        {member.status === "ACCEPTED" && <Button className="connect-button">MESSAGE</Button>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <FilterDropdown handleApply={handleApply} />
        </div>
    );
}

const MiniProfileView = ({ user, onStatusUpdate }) => {

    const { callApiFunction } = useApi();
    const [mutualConnections, setMutualConnections] = useState([]);
    const objectId = localStorage.getItem("objectId");
    // const [userStatus, setUserStatus] = useState(user.status); // State for user status

    const handleConnectRequest = async (contactRefId) => {
        const payload = {
            userrefid: objectId, // Replace with your actual objectId
            contact_ref_id: contactRefId,
            relationship_type: "FRIEND",

        };

        try {
            const response = await callApiFunction('SendConnectRequest', payload);

            if (response.status === "SUCCESS") {
                // setUserStatus("PENDING");
                onStatusUpdate("PENDING", contactRefId);
            } else {
                console.error('Failed to send connection request.');
            }
        } catch (err) {
            console.error('An error occurred while sending the connection request:', err);
        }
    };

    useEffect(() => {
        const fetchMutualConnections = async () => {
            try {

                const payload = { otherUserId: user.reference_id }
                const response = await callApiFunction('mutualConnection', payload);
                setMutualConnections(response || []); // Set mutual connections to the state               
            } catch (error) {
                console.error('Error fetching mutual connections', error);
            }
        };

        fetchMutualConnections();
    }, [user.reference_id]);




    return (
        <div className="mini-profile-container p-4 my-3">
            <div className="d-flex">

                <div className='Image_Container_Contacts'>
                    {/* <img className='User-photo' src={user.img} alt="userPhot" /> */}
                    <Avatar style={{ fontSize: "3rem" }}
                        className="w-100 h-100 cover-image User-photo"
                        src={user.img} alt={user?.first_name}>
                        {user?.first_name?.charAt(0).toUpperCase()}
                    </Avatar>
                </div>
                {/* User Information */}
                <div className='fixed-width-contactView'>
                    <h6 className="mb-0 heading-contacts-20px d-flex align-items-center gap-2">{user.first_name} {user.last_name}
                        {user.is_verified ?
                            <VerifiedIcon style={{ color: "#43A047" }} /> :
                            <NonVerifiedTickNew />
                        }
                    </h6>
                    <span className="my-1 heading-contacts-16px d-block">{user.role}</span>
                    <span className="mb-0 d-block sub-heading-contact">{user.subTitle}</span>
                    {/* <p>{user.mutualConnections} mutual connections</p> */}
                    <div className="my-profile-heading-content justify-content-between my-2">
                        <div className="d-flex align-items-center">
                            {mutualConnections.length > 0 && (
                                <>
                                    <AvatarGroup>
                                        {mutualConnections.map((connection, index) => (
                                            <Avatar
                                                key={index}
                                                alt={connection.first_name}
                                                src={connection.profile_url}
                                            />
                                        ))}
                                    </AvatarGroup>
                                    <div className="profile-meta-info">
                                        <GroupsIcon />{' '}
                                        <span className="color-blue">
                                            {mutualConnections.length} mutual Connections
                                        </span>
                                    </div>
                                </>
                            )}

                        </div>
                        <div className='d-flex align-items-center'>
                            <div className="profile-meta-info">
                                {user.status === null && (
                                    <Button className="connect-button" onClick={() => handleConnectRequest(user.reference_id)}>
                                        Connect
                                    </Button>
                                )}
                                {user.status === "PENDING" && (
                                    <Button className="connect-button">
                                        Pending
                                    </Button>
                                )}
                                {user.status === "ACCEPTED" && (
                                    <Button className="connect-button">
                                        Message
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FilterDropdown = ({ handleApply }) => {
    const { FilterOpen, handleClickFilter } = useQuantityContext();
    const [defaultDepartment, setDefaultDepartment] = useState(['Information technology (IT)', 'Healthcare', 'Retail', 'Finance', 'Education', 'Manufacturing', 'Business']);
    const [defaultJobTitles, setDefaultJobTitles] = useState([
        'CEO',
        'Manager',
        'Project Manager',
        'Software Engineer',
        'Business Analyst'
    ]);
    // State for storing filter selections
    const [filters, setFilters] = useState({
        // search: '',
        cardType: [],
        department: [],
        // experienceLevels: [],
        designation: [],
        country: [],
        state: [],
        city: [],
        postal_code: [],
        // radius: 2,
    });

    // Function to handle checkbox toggle for filters
    const handleCheckboxChange = (category, value) => {
        setFilters((prevState) => {
            const isChecked = prevState[category].includes(value);
            const newSelections = isChecked
                ? prevState[category].filter((item) => item !== value)  // Remove if unchecked
                : [...prevState[category], value];                      // Add if checked
            return { ...prevState, [category]: newSelections };
        });
    };

    // const handleTextChange = (event) => {
    //     const { name, value } = event.target;
    //     setFilters({ ...filters, [name]: value });
    // };

    // const handleRadiusChange = (event, newValue) => {
    //     setFilters({ ...filters, radius: newValue });
    // };

    // Function to add new custom department or designation
    const addNewItem = (category, newItem) => {
        if (newItem.trim()) {
            if (category === 'department') {
                setDefaultDepartment((prev) => [...prev, newItem]);
            } else if (category === 'designation') {
                setDefaultJobTitles((prev) => [...prev, newItem]);
            }
        }
    };

    const handleCommaSeparatedChange = (field, value) => {
        const valuesArray = value.split(',').map((item) => item.trim()).filter(Boolean);
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: valuesArray,
        }));
    };


    return (
        <div className={`filter-dropdown ${FilterOpen ? 'd-block' : 'd-none'}`}>
            <div className="p-3 border">
                <div className="d-flex justify-content-between align-items-center">
                    <h6>All Filters</h6>
                    <IconButton onClick={handleClickFilter}>
                        <CloseIcon />
                    </IconButton>
                </div>
                {/* <TextField
                    placeholder="Search for location, designation, category..."
                    variant="outlined"
                    fullWidth
                    className="my-3"
                    name="search"
                    value={filters.search}
                    onChange={handleTextChange}
                /> */}

                {/* Categories */}
                <h6>Category</h6>
                <FormGroup className="inline-checkbox-group">
                    {['Personal', 'Professional', 'Students'].map((category) => (
                        <FormControlLabel
                            key={category}
                            control={
                                <Checkbox
                                    checked={filters.cardType.includes(category)}
                                    onChange={() => handleCheckboxChange('cardType', category)}
                                />
                            }
                            label={category}
                        />
                    ))}
                </FormGroup>

                <hr />

                {/* Department */}
                <h6>Department</h6>
                <FormGroup className="inline-checkbox-group">
                    {defaultDepartment.map((department) => (
                        <FormControlLabel
                            key={department}
                            control={
                                <Checkbox
                                    checked={filters.department.includes(department)}
                                    onChange={() => handleCheckboxChange('department', department)}
                                />
                            }
                            label={department}
                        />
                    ))}
                    <SkillItemForNewAddItem
                        label="Type Department"
                        onAdd={(newDepartment) => addNewItem('department', newDepartment)}
                    />
                </FormGroup>
                <hr />

                {/* 
                <h6>Experience Level</h6>
                <FormGroup className="inline-checkbox-group">
                    {['Entry-level', 'Mid-level', 'Senior-level', 'Executive'].map((level) => (
                        <FormControlLabel
                            key={level}
                            control={
                                <Checkbox
                                    checked={filters.experienceLevels.includes(level)}
                                    onChange={() => handleCheckboxChange('experienceLevels', level)}
                                />
                            }
                            label={level}
                        />
                    ))}
                </FormGroup>
                <hr /> */}

                {/* Designation */}
                <h6>Designation</h6>
                <FormGroup className="inline-checkbox-group">
                    {defaultJobTitles.map((jobTitle) => (
                        <FormControlLabel
                            key={jobTitle}
                            control={
                                <Checkbox
                                    checked={filters.designation.includes(jobTitle)}
                                    onChange={() => handleCheckboxChange('designation', jobTitle)}
                                />
                            }
                            label={jobTitle}
                        />
                    ))}
                    <SkillItemForNewAddItem
                        label="Type Designation"
                        onAdd={(newDepartment) => addNewItem('designation', newDepartment)}
                    />
                </FormGroup>

                <hr />

                {/* Location */}
                <h6>Location</h6>
                <div className="row">
                    {/* Country Text Field */}
                    <div className="col-md-6 mb-3">
                        <TextField
                            label="Country"
                            variant="outlined"
                            placeholder="Enter countries, separated by commas"
                            value={filters.country.join(', ')}
                            onChange={(e) => handleCommaSeparatedChange('country', e.target.value)}
                            fullWidth
                        />
                    </div>

                    {/* State Text Field */}
                    <div className="col-md-6 mb-3">
                        <TextField
                            label="State"
                            variant="outlined"
                            placeholder="Enter states, separated by commas"
                            value={filters.state.join(', ')}
                            onChange={(e) => handleCommaSeparatedChange('state', e.target.value)}
                            fullWidth
                        />
                    </div>

                    {/* City Text Field */}
                    <div className="col-md-6 mb-3">
                        <TextField
                            label="City"
                            variant="outlined"
                            placeholder="Enter cities, separated by commas"
                            value={filters.city.join(', ')}
                            onChange={(e) => handleCommaSeparatedChange('city', e.target.value)}
                            fullWidth
                        />
                    </div>

                    {/* Postal Code Text Field */}
                    <div className="col-md-6 mb-3">
                        <TextField
                            label="Postal Code"
                            variant="outlined"
                            placeholder="Enter postal codes, separated by commas"
                            value={filters.postal_code.join(', ')}
                            onChange={(e) => handleCommaSeparatedChange('postal_code', e.target.value)}
                            fullWidth
                        />
                    </div>
                </div>


                {/* Radius Slider */}
                {/* <div className="my-3">
                    <h6>Radius</h6>
                    <Slider
                        value={filters.radius}
                        onChange={handleRadiusChange}
                        aria-labelledby="radius-slider"
                        min={0}
                        max={12}
                        valueLabelDisplay="on"
                        marks={[
                            { value: 0, label: '0' },
                            { value: 12, label: '12' },
                        ]}
                    />
                </div> */}

                {/* Reset and Apply Buttons */}
                <div className="d-flex justify-content-between mt-3">
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => setFilters({
                            cardType: [],
                            department: [],
                            // experienceLevels: [],
                            designation: [],
                            country: [],
                            state: [],
                            city: [],
                            postal_code: [],
                        })}
                        style={{ color: "#0044c6" }}
                    >
                        RESET
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => handleApply(filters)} style={{ backgroundColor: "#0044c6" }}>
                        APPLY
                    </Button>
                </div>
            </div>
        </div>
    );
};

const SkillItemForNewAddItem = ({ label, onAdd }) => {
    const [newItem, setNewItem] = useState('');

    const handleAdd = () => {
        if (newItem.trim()) {
            onAdd(newItem.trim());
            setNewItem('');
        }
    };

    return (
        <SkillBox>
            <TextField
                className="textfield-new-styles text-center"
                placeholder={label}
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        handleAdd();
                        e.preventDefault();
                    }
                }}
                sx={{
                    fontSize: '14px', lineHeight: '20.02px',
                    '& .MuiOutlinedInput-input': {
                        paddingLeft: 0
                    }
                }}
                variant="outlined"
                size="small"
                fullWidth
            />
            <IconButton className='flex-center' onClick={handleAdd} color='#0044C6'>
                <AddCircleRoundedIcon />
            </IconButton>
        </SkillBox>
    );
};

export default Connect;
